import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router } from '@angular/router';

import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TranslateModule } from '@ngx-translate/core';

import { AuthService } from '../core/auth.service';
import { SessionsService, SessionDto } from '../core/sessions.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  standalone: true,
  selector: 'app-home',
  imports: [
    CommonModule,
    TranslateModule,
    CardModule,
    ButtonModule,
    TagModule,
    SkeletonModule,
    ProgressSpinnerModule,
    DatePipe,
  ],
  styles: [
    `
      :host {
        display: block;
      }
      .header {
        display: grid;
        gap: 8px;
        margin-bottom: 16px;
      }
      .title {
        font-size: 28px;
        font-weight: 800;
        color: #111827;
        margin: 0;
      }
      .sub {
        color: #6b7280;
        line-height: 1.6;
        margin: 0;
        max-width: 780px;
      }

      .panel {
        background: #fff;
        border: 1px solid #e6e7eb;
        border-radius: 14px;
        padding: 18px;
        display: grid;
        gap: 16px;
      }

      .section-title {
        font-weight: 700;
        color: #1f2937;
        margin-bottom: 8px;
      }

      .kv {
        display: grid;
        gap: 6px;
      }
      .kv-row {
        display: grid;
        grid-template-columns: 140px 1fr;
        gap: 8px;
        align-items: center;
      }
      .kv-key {
        color: #6b7280;
        font-size: 13px;
      }
      .kv-val {
        color: #111827;
      }

      .chips {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }
      .chip {
        display: inline-flex;
        align-items: center;
        padding: 4px 10px;
        border-radius: 999px;
        background: #f3f4f6;
        color: #374151;
        border: 1px solid #e5e7eb;
        font-size: 12px;
      }

      .divider {
        height: 1px;
        background: #eef0f4;
        margin: 4px 0;
      }

      .actions {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
      }
      .login-cta,
      .actions :where(button, .p-button) {
        height: 40px;
        border-radius: 8px;
        font-weight: 700;
      }
      .login-cta {
        background: #2563eb;
        border: 1px solid #2a5bb1;
        color: #fff;
        padding-inline: 14px;
      }

      .stats-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 16px;
        margin-bottom: 24px;
      }

      .stat-card {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 20px;
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .stat-card.secondary {
        background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      }

      .stat-card.info {
        background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
      }

      .stat-card.success {
        background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
      }

      .stat-label {
        font-size: 13px;
        opacity: 0.9;
        font-weight: 500;
      }

      .stat-value {
        font-size: 32px;
        font-weight: 700;
        line-height: 1;
      }

      .permissions-group {
        margin-bottom: 16px;
      }

      .permissions-group-title {
        font-weight: 600;
        color: #4b5563;
        margin-bottom: 8px;
        font-size: 14px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .quick-actions {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: 12px;
      }

      .quick-actions .w-full {
        width: 100%;
      }

      @media (max-width: 640px) {
        .kv-row {
          grid-template-columns: 1fr;
        }
        .stats-grid {
          grid-template-columns: 1fr;
        }
        .quick-actions {
          grid-template-columns: 1fr;
        }
      }
    `,
  ],
  template: `
    <div class="header">
      <h1 class="title">{{ 'HOME.WELCOME' | translate }}</h1>
      <p class="sub">بوابة الدخول الموحّد (SSO) للأنظمة: HR و CuraLab</p>
    </div>

    <ng-container *ngIf="!auth.isAuthenticated(); else authed">
      <button
        class="login-cta"
        pButton
        type="button"
        icon="pi pi-sign-in"
        (click)="router.navigateByUrl('/login')"
      >
        تسجيل الدخول
      </button>
    </ng-container>

    <ng-template #authed>
      <!-- Statistics Cards -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">الجلسات النشطة</div>
          <div class="stat-value">
            <p-skeleton *ngIf="loadingSessions" width="40px" height="32px"></p-skeleton>
            <span *ngIf="!loadingSessions">{{ activeSessionsCount }}</span>
          </div>
        </div>
        <div class="stat-card secondary">
          <div class="stat-label">إجمالي الصلاحيات</div>
          <div class="stat-value">{{ perms.length }}</div>
        </div>
        <div class="stat-card info" *ngIf="expiresAt">
          <div class="stat-label">صلاحية التوكن</div>
          <div class="stat-value" style="font-size: 18px">
            {{ getTimeUntilExpiry() }}
          </div>
        </div>
      </div>

      <!-- Account Information Card -->
      <p-card styleClass="mb-3">
        <ng-template pTemplate="header">
          <div style="padding: 1rem; border-bottom: 1px solid #e5e7eb">
            <h3 style="margin: 0; font-size: 18px; font-weight: 600">
              معلومات الحساب
            </h3>
          </div>
        </ng-template>
        <div class="kv">
          <div class="kv-row">
            <div class="kv-key">اسم المستخدم</div>
            <div class="kv-val">{{ username || '—' }}</div>
          </div>
          <div class="kv-row">
            <div class="kv-key">البريد الإلكتروني</div>
            <div class="kv-val">{{ email || '—' }}</div>
          </div>
          <div class="kv-row" *ngIf="userId">
            <div class="kv-key">معرّف المستخدم</div>
            <div class="kv-val" style="font-family: monospace; font-size: 12px">
              {{ userId }}
            </div>
          </div>
          <div class="kv-row" *ngIf="expiresAt">
            <div class="kv-key">انتهاء صلاحية التوكن</div>
            <div class="kv-val">
              {{ expiresAt | date : 'medium' }}
            </div>
          </div>
        </div>
      </p-card>

      <!-- Permissions Card -->
      <p-card styleClass="mb-3" *ngIf="perms.length > 0">
        <ng-template pTemplate="header">
          <div style="padding: 1rem; border-bottom: 1px solid #e5e7eb">
            <h3 style="margin: 0; font-size: 18px; font-weight: 600">الصلاحيات</h3>
          </div>
        </ng-template>
        <div class="permissions-group" *ngFor="let group of permissionsBySystem">
          <div class="permissions-group-title">
            {{ group.system === 'auth' ? 'نظام الهوية' : group.system.toUpperCase() }}
          </div>
          <div class="chips">
            <p-tag
              *ngFor="let perm of group.permissions"
              [value]="perm"
              [severity]="getPermissionSeverity(perm)"
              styleClass="mb-2"
            ></p-tag>
          </div>
        </div>
        <ng-container *ngIf="perms.length === 0">
          <span class="kv-key">لا توجد صلاحيات متاحة</span>
        </ng-container>
      </p-card>

      <!-- Quick Actions Card -->
      <p-card>
        <ng-template pTemplate="header">
          <div style="padding: 1rem; border-bottom: 1px solid #e5e7eb">
            <h3 style="margin: 0; font-size: 18px; font-weight: 600">إجراءات سريعة</h3>
          </div>
        </ng-template>
        <div class="quick-actions">
          <button
            pButton
            label="لوحة التحكم"
            icon="pi pi-th-large"
            class="p-button-secondary w-full"
            [disabled]="!hasPermission('perm:auth:*')"
            (click)="router.navigateByUrl('/users')"
          ></button>
          <button
            pButton
            label="HR Demo"
            icon="pi pi-users"
            class="p-button-secondary w-full"
            [disabled]="!hasPermission('hr:employees.read')"
            (click)="router.navigateByUrl('/hr')"
          ></button>
          <button
            pButton
            label="جلساتي"
            icon="pi pi-clock"
            class="w-full"
            (click)="router.navigateByUrl('/sessions')"
          ></button>
          <button
            pButton
            label="تغيير كلمة المرور"
            icon="pi pi-key"
            class="p-button-warning w-full"
            (click)="router.navigateByUrl('/change-password')"
          ></button>
          <button
            pButton
            label="تسجيل الخروج"
            icon="pi pi-sign-out"
            class="p-button-danger w-full"
            (click)="logout()"
          ></button>
        </div>
      </p-card>
    </ng-template>
  `,
})
export class HomeComponent implements OnInit, OnDestroy {
  auth = inject(AuthService);
  router = inject(Router);
  sessionsService = inject(SessionsService);

  private destroy$ = new Subject<void>();
  sessions: SessionDto[] = [];
  loadingSessions = true;

  ngOnInit() {
    this.loadSessions();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadSessions() {
    this.loadingSessions = true;
    this.sessionsService
      .mySessions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (sessions) => {
          this.sessions = sessions;
          this.loadingSessions = false;
        },
        error: () => {
          this.loadingSessions = false;
        },
      });
  }

  get username(): string {
    const c = this.auth.claims;
    return c?.name || c?.preferred_username || c?.sub || '';
  }

  get email(): string {
    return this.auth.claims?.email || '';
  }

  get userId(): string {
    return this.auth.claims?.sub || '';
  }

  get perms(): string[] {
    const p = this.auth.claims?.perm;
    if (Array.isArray(p)) return p;
    if (typeof p === 'string') return [p];
    return [];
  }

  get permissionsBySystem(): Array<{ system: string; permissions: string[] }> {
    const grouped: Record<string, string[]> = {};
    this.perms.forEach((perm) => {
      const [system] = perm.split(':');
      if (!grouped[system]) {
        grouped[system] = [];
      }
      grouped[system].push(perm);
    });
    return Object.keys(grouped)
      .sort()
      .map((system) => ({
        system,
        permissions: grouped[system].sort(),
      }));
  }

  get activeSessionsCount(): number {
    return this.sessions.filter((s) => s.isActive && !s.isRevoked).length;
  }

  get expiresAt(): Date | null {
    const expSec = this.auth.claims?.exp;
    return expSec ? new Date(expSec * 1000) : null;
  }

  getTimeUntilExpiry(): string {
    if (!this.expiresAt) return '—';
    const now = new Date();
    const diff = this.expiresAt.getTime() - now.getTime();
    if (diff <= 0) return 'منتهي';
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days} يوم`;
    if (hours > 0) return `${hours} ساعة`;
    return `${minutes} دقيقة`;
  }

  getPermissionSeverity(perm: string): string {
    if (perm.endsWith(':*')) return 'success';
    if (perm.includes('write') || perm.includes('create') || perm.includes('delete'))
      return 'warning';
    if (perm.includes('read')) return 'info';
    return '';
  }

  hasPermission(perm: string): boolean {
    return this.auth.hasPermission(perm);
  }

  async logout() {
    await this.auth.logout();
  }
}
